import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { toast, confirm as swalConfirm } from '../utils/swal';
import AppHeader from './AppHeader';
import EventPhotoUpload from './EventPhotoUpload';
import SeatBlockManager from './SeatBlockManager';
import InvitationCodeManager from './InvitationCodeManager';
import './EventManagement.css';

const EventManagement = ({ onBack, onCreateNew, onEdit, onGoToProfile, onGoToAdminPanel, onGoToVenueManage, onGoToQrScanner }) => {
  const [events, setEvents]         = useState([]);
  const [venues, setVenues]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('all');
  const [viewMode, setViewMode]     = useState('card');
  const [photoEvent, setPhotoEvent] = useState(null);
  const [blockEvent, setBlockEvent] = useState(null);
  const [codeEvent, setCodeEvent]   = useState(null);

  const headerNavProps = {
    onGoToProfile,
    onGoToEventManagement: () => { setPhotoEvent(null); setBlockEvent(null); setCodeEvent(null); },
    onGoToAdminPanel,
    onGoToVenueManage,
    onGoToQrScanner,
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventsData, venuesData] = await Promise.all([api.getEvents(), api.getVenues()]);
      setEvents(eventsData);
      setVenues(venuesData);
    } catch (err) {
      toast.error('Veriler yüklenemedi!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId, eventName) => {
    const del = await swalConfirm.delete(`"${eventName}" etkinliğini`);
    if (!del.isConfirmed) return;
    try {
      await api.deleteEvent(eventId);
      toast.success('Etkinlik silindi!');
      loadData();
    } catch { toast.error('Etkinlik silinemedi!'); }
  };

  const handleToggleActive = async (event) => {
    try {
      await api.updateEvent(event.id, { ...event, isActive: !event.isActive });
      toast.success(`Etkinlik ${!event.isActive ? 'aktif' : 'pasif'} hale getirildi!`);
      loadData();
    } catch { toast.error('Durum değiştirilemedi!'); }
  };

  const getVenueName     = (venueId) => venues.find(v => v.id === venueId)?.name ?? 'Bilinmeyen Salon';
  const getVenueObj      = (venueId) => venues.find(v => v.id === venueId);
  const getVenueCapacity = (venueId) => {
    const venue = getVenueObj(venueId);
    if (!venue?.sections) return 0;
    return venue.sections.reduce((sum, s) => sum + ((s.endRow - s.startRow + 1) * s.seatsPerRow), 0);
  };

  const filterEvents = () => {
    const now = new Date();
    switch (filter) {
      case 'active': return events.filter(e => e.isActive);
      case 'past':   return events.filter(e => new Date(e.date) < now);
      case 'future': return events.filter(e => new Date(e.date) >= now);
      default:       return events;
    }
  };

  if (photoEvent) {
    return (
      <EventPhotoUpload
        event={photoEvent}
        onBack={() => setPhotoEvent(null)}
        onUpdate={(updatedEvent) => {
          setEvents(prev => prev.map(e => e.id === updatedEvent.id ? { ...e, ...updatedEvent } : e));
          setPhotoEvent(prev => ({ ...prev, ...updatedEvent }));
        }}
        {...headerNavProps}
      />
    );
  }

  if (codeEvent) {
    return <InvitationCodeManager event={codeEvent} onBack={() => setCodeEvent(null)} {...headerNavProps} />;
  }

  if (blockEvent) {
    const venue = getVenueObj(blockEvent.venueId);
    if (!venue) { toast.error('Salon bilgisi bulunamadı!'); setBlockEvent(null); }
    else {
      return <SeatBlockManager event={blockEvent} venue={venue} onBack={() => setBlockEvent(null)} {...headerNavProps} />;
    }
  }

  const filteredEvents = filterEvents();

  return (
    <div className="event-management">
      <AppHeader title="Etkinlik Yönetimi" onBack={onBack} {...headerNavProps} />

      <div className="controls-bar">
        <div className="filter-tabs">
          {[
            { key: 'all',    label: `Tümü (${events.length})`,                                                          icon: 'fa-list' },
            { key: 'active', label: `Aktif (${events.filter(e => e.isActive).length})`,                                 icon: 'fa-circle-check' },
            { key: 'future', label: `Gelecek (${events.filter(e => new Date(e.date) >= new Date()).length})`,            icon: 'fa-calendar' },
            { key: 'past',   label: `Geçmiş (${events.filter(e => new Date(e.date) < new Date()).length})`,             icon: 'fa-box-archive' },
          ].map(f => (
            <button key={f.key} className={`filter-tab ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
              <i className={`fa-solid ${f.icon}`} /> {f.label}
            </button>
          ))}
        </div>
        <div className="controls-right">
          <div className="view-toggle">
            <button className={`view-btn ${viewMode === 'card' ? 'active' : ''}`} onClick={() => setViewMode('card')} title="Kart">
              <i className="fa-solid fa-table-cells-large" />
            </button>
            <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grid">
              <i className="fa-solid fa-grip" />
            </button>
          </div>
          <button className="create-new-btn" onClick={onCreateNew}>
            <i className="fa-solid fa-plus" /> Yeni Etkinlik
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading"><i className="fa-solid fa-spinner fa-spin" /> Yükleniyor...</div>
      ) : (
        <div className={`events-list ${viewMode}`}>
          {filteredEvents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><i className="fa-solid fa-masks-theater" /></div>
              <h3>Etkinlik Bulunamadı</h3>
              <p>Yeni bir etkinlik oluşturarak başlayın!</p>
              <button onClick={onCreateNew} className="create-empty-btn">
                <i className="fa-solid fa-plus" /> İlk Etkinliği Oluştur
              </button>
            </div>
          ) : (
            filteredEvents.map(event => (
              <div key={event.id} className="event-card">
                {(event.mainPhoto || event.venueMainPhoto) && (
                  <div className="event-card-photo">
                    <img
                      src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${event.mainPhoto || event.venueMainPhoto}`}
                      alt={event.name}
                    />
                  </div>
                )}

                <div className="event-header">
                  <h3>{event.name}</h3>
                  <span className={`status-badge ${event.isActive ? 'active' : 'inactive'}`}>
                    {event.isActive
                      ? <><i className="fa-solid fa-circle" style={{color:'#22c55e', fontSize:'0.6em'}} /> Aktif</>
                      : <><i className="fa-solid fa-circle" style={{color:'#6b7280', fontSize:'0.6em'}} /> Pasif</>}
                  </span>
                </div>

                <div className="event-body">
                  <div className="event-detail">
                    <span className="detail-icon"><i className="fa-solid fa-building-columns" /></span>
                    <span>{getVenueName(event.venueId)}</span>
                  </div>
                  <div className="event-detail">
                    <span className="detail-icon"><i className="fa-solid fa-calendar" /></span>
                    <span>{new Date(event.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                  <div className="event-detail">
                    <span className="detail-icon"><i className="fa-solid fa-clock" /></span>
                    <span>{new Date(event.date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>

                  {(event.saleStartDate || event.saleEndDate) && (() => {
                    const now = new Date();
                    const saleStart = event.saleStartDate ? new Date(event.saleStartDate) : null;
                    const saleEnd   = event.saleEndDate   ? new Date(event.saleEndDate)   : null;
                    let label, cls;
                    if (saleStart && saleStart > now) {
                      label = `Satış başlangıç: ${saleStart.toLocaleDateString('tr-TR')}`;
                      cls = 'sale-pending';
                    } else if (saleEnd && saleEnd < now) {
                      label = `Satışlar Kapandı (${saleEnd.toLocaleDateString('tr-TR')})`;
                      cls = 'sale-closed';
                    } else {
                      label = `Satışlar Açık${saleEnd ? ` → ${saleEnd.toLocaleDateString('tr-TR')}` : ''}`;
                      cls = 'sale-open';
                    }
                    return <div className={`event-detail sale-status-badge ${cls}`}><span>{label}</span></div>;
                  })()}

                  <div className="event-detail">
                    <span className="detail-icon"><i className="fa-solid fa-chair" /></span>
                    <span>{getVenueCapacity(event.venueId)} koltuk</span>
                  </div>
                  {event.description && <div className="event-description">{event.description}</div>}
                  {event.enableDoubleClickSelection && (
                    <div className="event-feature">
                      <i className="fa-solid fa-user-group" /> Çift Koltuk Modu Aktif
                    </div>
                  )}
                </div>

                <div className="event-actions">
                  <button onClick={() => onEdit(event)} className="action-btn edit-btn" title="Düzenle">
                    <i className="fa-solid fa-pen" />
                  </button>
                  <button onClick={() => setPhotoEvent(event)} className="action-btn photo-btn" title="Fotoğraf">
                    <i className="fa-solid fa-camera" />
                  </button>
                  <button onClick={() => setBlockEvent(event)} className="action-btn block-btn" title="Koltuk Düzeni">
                    <i className="fa-solid fa-table-cells" />
                  </button>
                  <button onClick={() => setCodeEvent(event)} className="action-btn code-btn" title="Erişim Kodları">
                    <i className="fa-solid fa-key" />
                  </button>
                  <button onClick={() => handleToggleActive(event)}
                    className={`action-btn toggle-btn ${event.isActive ? 'deactivate' : 'activate'}`}
                    title={event.isActive ? 'Pasif Yap' : 'Aktif Yap'}>
                    <i className={`fa-solid ${event.isActive ? 'fa-pause' : 'fa-play'}`} />
                  </button>
                  <button onClick={() => handleDelete(event.id, event.name)} className="action-btn delete-btn" title="Sil">
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default EventManagement;
